import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup, linkWithCredential } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { APP_VERSION } from '../version';
import { useT } from '../i18n';
// Token reka bentuk v3.9 (--s-*). Login ialah early-return SEBELUM shell utama,
// jadi ia tidak mewarisi import CSS shell — kena import sendiri (corak sama
// FlowPayView). Tanpa ini, setiap var(--s-*) jatuh telus dan halaman pecah.
import '../design/tokens.css';

// Kod ejen: cookie (ditulis oleh pembaca ?ref= global App.jsx) → localStorage →
// alamat URL. Baca alamat sebagai jaring terakhir supaya pelanggan yang bertukar
// browser (WhatsApp → Safari) tak hilang ejen. Lihat utils/refLink.js.
import { getActiveRef } from '../utils/refLink';

// Normalize Malaysian phone to +60XXXXXXXXX format
function normalizeMyPhone(raw) {
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    if (digits.startsWith('60')) return `+${digits}`;
    if (digits.startsWith('0')) return `+60${digits.slice(1)}`;
    return `+60${digits}`;
}

function isValidMyPhone(raw) {
    const normalized = normalizeMyPhone(raw);
    // +60 followed by 9-10 digits (MY mobile: 01x-xxx-xxxx)
    return /^\+60\d{9,10}$/.test(normalized);
}

// ─── SUIS BUTANG GOOGLE (sorok dari pelanggan sehingga release extension v3.8.2) ───
// Sebab: pelanggan yang daftar guna Google SEBELUM extension v3.8.2 ship akan dapat
// akaun tanpa password → TAK BOLEH log masuk extension (butang Google extension belum
// sampai ke mereka). Sorok dulu; butang tetap muncul di localhost & via /login?google=1 (admin).
// ⚠️ FLIP ke true MASA RELEASE v3.8.2 — lihat CLAUDE.md § Pending Deploy.
const SHOW_GOOGLE_LOGIN = true;

const ADMIN_WA_NUMBER = '601135011231';

/* Mesej WhatsApp kepada admin SENGAJA kekal BM dan bukan t() —
   keputusan user 2026-08-16: penerima mesej ini ialah admin
   (orang Melayu), bukan pelanggan, jadi ia tidak ikut bahasa
   paparan pelanggan. Hanya LABEL butang yang diterjemah. */
const ADMIN_WA_MESSAGE = 'Salam admin, saya ingin mendaftar SESAAT tetapi tiada pautan rujukan daripada ejen. Mohon bantuan.';

/* ─── Mockup UI/UX Workspace SESAAT (design SAHAJA, tiada logic) ───
   Arahan user 2026-08-21: Section 1 panel kanan = paparkan skrin Workspace
   SEBENAR (tab Generate + pemilih Formula). Warna DITETAPKAN terang — ia
   "tangkap skrin" yang terapung atas panel gelap login, kekal terang walau
   tema bertukar. Thumbnail = blok warna placeholder + teks hook (kita tiada
   fail gambar sebenar); susun atur & struktur ikut skrin sebenar. */
/* img = JPG KECIL (~2-13KB) di public/salespage/lgn/ — dihidang oleh Firebase
   Hosting (di-cache CDN), BUKAN video, BUKAN Firebase Storage. Hampir zero cost.
   4 daripada fail salespage sedia ada (dikecilkan); 2 (ai16saat, smartcustom)
   hanya ada video, jadi 1 bingkai diambil & disimpan sebagai JPG kecil. */
const WS_FORMULAS = [
    { name: 'AI8SAAT', badge: '', sub: '1 scene · 3 sets', dur: '0:08', img: '/salespage/lgn/ai8saat.jpg', bg: '#8a8f98' },
    { name: 'AI16SAAT', badge: 'BETA', sub: '2 scenes · 16s', dur: '0:16', img: '/salespage/lgn/ai16saat.jpg', bg: '#b79b83' },
    { name: 'HOME NICHE', badge: 'BETA', sub: '2 scenes · home niche', dur: '0:16', img: '/salespage/lgn/homeniche.jpg', bg: '#c9b6a6' },
    { name: 'GOYANG', badge: 'BETA', sub: 'Motion template', dur: '0:08', img: '/salespage/lgn/goyang.jpg', bg: '#d98fa4' },
    { name: 'SMART', badge: 'NEW', sub: 'Your own prompt', dur: '0:08', img: '/salespage/lgn/smartcustom.jpg', bg: '#8a9478' },
    { name: 'FLOWNITY', badge: 'NEW', sub: '5 scenes, continuous', dur: '0:40', img: '/salespage/lgn/flownity.jpg', bg: '#6f8f6a' },
];

const WorkspaceMock = () => (
    <div className="lgn-app">
        <div className="lgn-app-side">
            <div className="lgn-app-side-ic"><span className="material-symbols-outlined">menu_open</span></div>
            <div className="lgn-app-logo"><span className="material-symbols-outlined" style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}>extension</span></div>
            {['grid_view', 'layers', 'calendar_month', 'widgets', 'bolt', 'storefront', 'group', 'sell', 'description', 'edit', 'public', 'settings'].map((ic, i) => (
                <div key={i} className="lgn-app-side-ic"><span className="material-symbols-outlined">{ic}</span></div>
            ))}
        </div>
        <div className="lgn-app-main">
            <div className="lgn-app-head">
                <span className="lgn-app-title">Workspace</span>
                <span className="lgn-app-group"><span className="material-symbols-outlined">folder</span>Main Group <b>23</b><span className="material-symbols-outlined">expand_more</span></span>
            </div>
            <div className="lgn-app-tabs">
                <span className="lgn-app-tab on"><span className="material-symbols-outlined">grid_view</span>Generate</span>
                <span className="lgn-app-tab"><span className="material-symbols-outlined">smart_toy</span>My Agent <b>3</b></span>
                <span className="lgn-app-tab"><span className="material-symbols-outlined">bolt</span>Flow <b>13</b></span>
                <span className="lgn-app-tab"><span className="material-symbols-outlined">music_note</span>Schedule <b>7</b></span>
            </div>
            <div className="lgn-app-box">
                <div className="lgn-app-boxtop">
                    <span className="lgn-app-add"><span className="material-symbols-outlined">add</span>Add Product</span>
                    <span className="lgn-app-dim">No product attached yet.</span>
                </div>
                <div className="lgn-app-formlabel">Formula <em>— each formula produces a different video style.</em></div>
                <div className="lgn-app-formulas">
                    <div className="lgn-fcard">
                        <div className="lgn-fthumb lgn-fthumb--new"><span className="material-symbols-outlined">add</span></div>
                        <div className="lgn-fname">Create your own</div>
                        <div className="lgn-fsub">Yours only</div>
                    </div>
                    {WS_FORMULAS.map((f, i) => (
                        <div key={i} className="lgn-fcard">
                            <div className="lgn-fthumb" style={{ backgroundColor: f.bg, backgroundImage: `url(${f.img})` }}>
                                <span className="lgn-fdur">{f.dur}</span>
                            </div>
                            <div className="lgn-fname">{f.name}{f.badge && <span className={`lgn-fbadge lgn-fbadge--${f.badge.toLowerCase()}`}>{f.badge}</span>}</div>
                            <div className="lgn-fsub">{f.sub}</div>
                        </div>
                    ))}
                </div>
                <div className="lgn-app-bottom">
                    <span className="lgn-app-pill"><span className="material-symbols-outlined">person</span>No avatar<span className="material-symbols-outlined">expand_more</span></span>
                    <span className="lgn-app-pill"><span className="material-symbols-outlined">tune</span>Pro Mode<i className="lgn-app-toggle" /></span>
                </div>
            </div>
        </div>
    </div>
);

const Login = () => {
    const t = useT();
    // Suis Google dinilai setiap render: production ikut SHOW_GOOGLE_LOGIN;
    // localhost & ?google=1 (pintu admin) sentiasa nampak.
    let showGoogle = SHOW_GOOGLE_LOGIN;
    try {
        showGoogle = SHOW_GOOGLE_LOGIN
            || window.location.hostname === 'localhost'
            || window.location.hostname === '127.0.0.1'
            || new URLSearchParams(window.location.search).has('google');
    } catch (_) { /* kekal ikut suis */ }

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    // Bila butang Google tersembunyi, terus tunjuk borang email (elak klik sia-sia)
    const [showEmailForm, setShowEmailForm] = useState(!showGoogle);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetMsg, setResetMsg] = useState({ type: '', text: '' });
    // Kod ejen — gate signup gantung pada ini. Login tak terjejas (user yg dah
    // login pasti dah dirujuk dulu).
    // ⚠️ Kena SEMAK SEMULA, bukan baca sekali: pembaca ?ref= global dalam App.jsx
    // jalan dalam useEffect (selepas anak render), jadi bacaan pertama boleh kosong
    // walaupun kod ejen ada dalam alamat → dulu banner "Tiada pautan rujukan" muncul
    // silap & butang Daftar tersekat kekal.
    const [refCode, setRefCode] = useState(() => getActiveRef());
    useEffect(() => {
        if (refCode) return;
        let tries = 0;
        const id = setInterval(() => {
            const found = getActiveRef();
            if (found) setRefCode(found);
            if (found || ++tries >= 20) clearInterval(id); // maks ~3s
        }, 150);
        return () => clearInterval(id);
    }, [refCode]);
    // ─── Google Sign-In ───
    const [googleLoading, setGoogleLoading] = useState(false);
    // linkData = { email, pendingCred } bila email dah wujud sbg akaun password →
    // kena "sambung" (link) Google ke akaun SAMA supaya data/lesen tak terpisah.
    const [linkData, setLinkData] = useState(null);
    const [linkPassword, setLinkPassword] = useState('');
    const [linkError, setLinkError] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);


    const handleGoogleSignIn = async () => {
        setError('');
        setGoogleLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: 'select_account' });
            await signInWithPopup(auth, provider);
            // Berjaya — onAuthStateChanged (App.jsx) ambil alih. Pengguna BARU akan
            // melalui gate "Lengkapkan Pendaftaran" (semak ref ejen + kutip WhatsApp).
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/account-exists-with-different-credential') {
                // Email ni dah ada akaun email+password → minta password sekali utk link.
                const pendingCred = GoogleAuthProvider.credentialFromError(err);
                setLinkData({ email: err.customData?.email || '', pendingCred });
                setLinkPassword('');
                setLinkError('');
            } else if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
                // Pengguna tutup popup — bukan error.
            } else if (err.code === 'auth/popup-blocked') {
                setError(t('login.err.popupBlocked'));
            } else if (err.code === 'auth/operation-not-allowed') {
                setError(t('login.err.googleDisabled'));
            } else {
                setError(t('login.err.googleFailed'));
            }
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleLinkSubmit = async (e) => {
        e.preventDefault();
        if (!linkData) return;
        setLinkLoading(true);
        setLinkError('');
        try {
            const cred = await signInWithEmailAndPassword(auth, linkData.email, linkPassword);
            if (linkData.pendingCred) {
                try {
                    await linkWithCredential(cred.user, linkData.pendingCred);
                } catch (linkErr) {
                    // Dah linked / credential dipakai — bukan fatal, user tetap log masuk.
                    if (linkErr.code !== 'auth/provider-already-linked' && linkErr.code !== 'auth/credential-already-in-use') {
                        console.warn('[Login] Gagal link Google (non-fatal):', linkErr?.message);
                    }
                }
            }
            // Berjaya log masuk — App.jsx ambil alih.
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setLinkError(t('login.err.wrongPassword'));
            } else if (err.code === 'auth/too-many-requests') {
                setLinkError(t('login.err.tooManyAttempts'));
            } else {
                setLinkError(t('login.err.linkFailed'));
            }
        } finally {
            setLinkLoading(false);
        }
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (isRegistering) {
                // Backup defense — block signup tanpa ref ejen walau UI bypass.
                // Auth account TIDAK akan dicipta kalau ref tiada.
                const refAtSignup = getActiveRef();
                if (!refAtSignup) {
                    setError(t('login.err.refRequired'));
                    setLoading(false);
                    return;
                }
                if (!isValidMyPhone(whatsapp)) {
                    setError(t('login.err.invalidPhone'));
                    setLoading(false);
                    return;
                }
                const cred = await createUserWithEmailAndPassword(auth, email, password);
                // Save WhatsApp + email to Firestore user doc
                try {
                    const profile = {
                        email: cred.user.email,
                        whatsappNumber: normalizeMyPhone(whatsapp),
                        createdAt: serverTimestamp()
                    };
                    await setDoc(doc(db, 'users', cred.user.uid), profile, { merge: true });
                } catch (dbErr) {
                    console.warn('[Login] Failed to save user profile:', dbErr.message);
                }
                // referredBy ditulis ASING: rules benarkan ia diisi SEKALI sahaja (bila kosong). Kalau ia
                // ditolak, WhatsApp + email di atas tetap tersimpan. bindReferral di bawah = sumber rasmi.
                // ⚠️ KEMBAR: firestore.rules users update (referredBy write-once) · CompleteRegistration.jsx.
                if (refAtSignup) {
                    try {
                        await setDoc(doc(db, 'users', cred.user.uid), { referredBy: refAtSignup }, { merge: true });
                    } catch (refDbErr) {
                        console.warn('[Login] referredBy not saved (bindReferral will set it):', refDbErr.message);
                    }
                }
                // Bind referral server-side (atomic — creates referrals doc + validates self-referral)
                if (refAtSignup) {
                    try {
                        await httpsCallable(functions, 'bindReferral')({ refCode: refAtSignup });
                    } catch (refErr) {
                        // Non-fatal — auth listener will retry as fallback
                        console.warn('[Login] bindReferral failed (non-fatal):', refErr?.message);
                    }
                }
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/email-already-in-use') {
                setError(t('login.err.emailInUse'));
            } else if (err.code === 'auth/weak-password') {
                setError(t('login.err.weakPassword'));
            } else if (err.code === 'auth/invalid-email') {
                setError(t('login.err.invalidEmail'));
            } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setError(t('login.err.badCredentials'));
            } else if (err.code === 'auth/too-many-requests') {
                setError(t('login.err.tooManyAttempts'));
            } else {
                setError(isRegistering ? t('login.err.registerFailed') : t('login.err.loginFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (!resetEmail) return;
        setResetLoading(true);
        setResetMsg({ type: '', text: '' });
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetMsg({ type: 'success', text: t('login.resetSent') });
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/user-not-found') {
                setResetMsg({ type: 'error', text: t('login.err.emailNotFound') });
            } else if (err.code === 'auth/too-many-requests') {
                setResetMsg({ type: 'error', text: t('login.err.tooManyRequests') });
            } else if (err.code === 'auth/invalid-email') {
                setResetMsg({ type: 'error', text: t('login.err.invalidEmail') });
            } else {
                setResetMsg({ type: 'error', text: t('login.err.resetFailed') });
            }
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="s-root lgn-page">
            {/* ===== KIRI — Borang log masuk ===== */}
            <div className="lgn-left">
                <div className="lgn-left-inner">
                    {/* Jenama */}
                    <div className="lgn-brand">
                        <div className="lgn-logo">
                            <span className="material-symbols-outlined" style={{ fontSize: 20, fontVariationSettings: "'FILL' 1" }}>bolt</span>
                        </div>
                        <span className="lgn-brandname">SESAAT</span>
                    </div>

                    {linkData ? (
                        /* ---- Sambungkan Akaun Google (email dah ada akaun password) ---- */
                        <div className="lgn-formarea">
                            <h2 className="lgn-title">{t('login.linkTitle')}</h2>
                            <p className="lgn-subtitle">
                                {t('login.linkDesc', { emel: linkData.email })}
                            </p>

                            {linkError && (
                                <div className="lgn-msg lgn-msg-error">
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>error</span>
                                    {linkError}
                                </div>
                            )}

                            <form onSubmit={handleLinkSubmit} className="lgn-form">
                                <input
                                    type="password"
                                    value={linkPassword}
                                    onChange={(e) => setLinkPassword(e.target.value)}
                                    className="lgn-input"
                                    placeholder={t('login.existingPasswordPlaceholder')}
                                    required
                                    autoComplete="current-password"
                                />
                                <button type="submit" disabled={linkLoading} className="lgn-submit">
                                    {linkLoading ? <span className="lgn-spinner"></span> : t('login.linkSubmit')}
                                </button>
                            </form>

                            <button
                                type="button"
                                onClick={() => { setShowForgotPassword(true); setResetEmail(linkData.email); setResetMsg({ type: '', text: '' }); setLinkData(null); }}
                                className="lgn-link"
                            >
                                {t('login.forgot')}
                            </button>

                            <button type="button" onClick={() => setLinkData(null)} className="lgn-back">
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_back</span>
                                {t('common.back')}
                            </button>
                        </div>
                    ) : !showEmailForm && !showForgotPassword ? (
                        /* ---- Butang masuk (entry) ---- */
                        <div className="lgn-entry">
                            {showGoogle && (<>
                            <button className="lgn-google" onClick={handleGoogleSignIn} disabled={googleLoading}>
                                {googleLoading ? (
                                    <span className="lgn-spinner"></span>
                                ) : (
                                    <>
                                        <svg width="18" height="18" viewBox="0 0 24 24">
                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                        </svg>
                                        {t('login.continueGoogle')}
                                    </>
                                )}
                            </button>

                            <div className="lgn-divider">
                                <span>{t('login.or')}</span>
                            </div>
                            </>)}

                            <button
                                className="lgn-btn-ghost"
                                onClick={() => setShowEmailForm(true)}
                            >
                                {t('login.continueEmail')}
                            </button>

                            <p className="lgn-terms">
                                {t('login.terms')}
                            </p>
                        </div>
                    ) : showForgotPassword ? (
                        /* ---- Lupa kata laluan ---- */
                        <div className="lgn-formarea">
                            <h2 className="lgn-title">{t('login.resetTitle')}</h2>
                            <p className="lgn-subtitle">{t('login.resetDesc')}</p>

                            {resetMsg.text && (
                                <div className={`lgn-msg ${resetMsg.type === 'error' ? 'lgn-msg-error' : 'lgn-msg-success'}`}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                                        {resetMsg.type === 'error' ? 'error' : 'check_circle'}
                                    </span>
                                    {resetMsg.text}
                                </div>
                            )}

                            <form onSubmit={handleForgotPassword} className="lgn-form">
                                <input
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    className="lgn-input"
                                    placeholder={t('login.emailPlaceholder')}
                                    required
                                />
                                <button type="submit" disabled={resetLoading} className="lgn-submit">
                                    {resetLoading ? <span className="lgn-spinner"></span> : t('login.sendReset')}
                                </button>
                            </form>

                            <button
                                type="button"
                                onClick={() => { setShowForgotPassword(false); setResetMsg({ type: '', text: '' }); }}
                                className="lgn-back"
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_back</span>
                                {t('login.backToLogin')}
                            </button>
                        </div>
                    ) : (
                        /* ---- Borang Emel / Kata laluan ---- */
                        <div className="lgn-formarea">
                            <h2 className="lgn-title">
                                {isRegistering ? t('login.registerTitle') : t('login.login')}
                            </h2>

                            {/* Sepanduk ref ejen — page DAFTAR sahaja */}
                            {isRegistering && refCode && (
                                <div className="lgn-ref-ok">
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>verified</span>
                                    <span>{t('login.refOk', { kod: refCode })}</span>
                                </div>
                            )}
                            {/* Kotak amaran "tiada ejen" — teks SENGAJA BM (bukan t()):
                                yang nampak ini ialah pelanggan Malaysia yang cuba daftar
                                tanpa pautan ejen; mesej WhatsApp (ADMIN_WA_MESSAGE) pun BM.
                                Arahan user 2026-08-21. */}
                            {isRegistering && !refCode && (
                                <div className="lgn-ref-missing">
                                    <div className="lgn-ref-missing-head">
                                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>error</span>
                                        <strong>Tiada pautan rujukan ejen</strong>
                                    </div>
                                    <p className="lgn-ref-missing-body">
                                        Pendaftaran SESAAT memerlukan pautan rujukan daripada ejen. Sila hubungi admin untuk mendapatkannya.
                                    </p>
                                    <a
                                        href={`https://wa.me/${ADMIN_WA_NUMBER}?text=${encodeURIComponent(ADMIN_WA_MESSAGE)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="lgn-wa-btn"
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>chat</span>
                                        Hubungi Admin di WhatsApp
                                    </a>
                                </div>
                            )}

                            {error && (
                                <div className="lgn-msg lgn-msg-error">
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>error</span>
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleAuth} className="lgn-form">
                                <input
                                    id="login-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="lgn-input"
                                    placeholder={t('login.emailPlaceholder')}
                                    required
                                    autoComplete="email"
                                />
                                {isRegistering && (
                                    <input
                                        id="login-whatsapp"
                                        type="tel"
                                        value={whatsapp}
                                        onChange={(e) => setWhatsapp(e.target.value)}
                                        className="lgn-input"
                                        placeholder={t('login.whatsappPlaceholder')}
                                        required
                                        autoComplete="tel"
                                    />
                                )}
                                <input
                                    id="login-password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="lgn-input"
                                    placeholder={t('login.passwordPlaceholder')}
                                    required
                                    autoComplete={isRegistering ? 'new-password' : 'current-password'}
                                />
                                {isRegistering && (
                                    <p className="lgn-note">
                                        {t('login.whatsappNote')}
                                    </p>
                                )}
                                <button
                                    id="login-submit"
                                    type="submit"
                                    disabled={loading || (isRegistering && !refCode)}
                                    className="lgn-submit"
                                >
                                    {loading
                                        ? <span className="lgn-spinner"></span>
                                        : (isRegistering
                                            ? (refCode ? t('login.register') : t('login.registerLocked'))
                                            : t('login.login'))}
                                </button>
                            </form>

                            {!isRegistering && (
                                <button
                                    type="button"
                                    onClick={() => { setShowForgotPassword(true); setResetEmail(email); setResetMsg({ type: '', text: '' }); }}
                                    className="lgn-link"
                                >
                                    {t('login.forgot')}
                                </button>
                            )}

                            <div className="lgn-toggle-row">
                                <span>{isRegistering ? t('login.haveAccount') : t('login.noAccount')}</span>
                                <button
                                    type="button"
                                    onClick={() => { setIsRegistering(!isRegistering); setError(''); }}
                                    className="lgn-toggle"
                                >
                                    {isRegistering ? t('login.login') : t('login.register')}
                                </button>
                            </div>

                            {showGoogle && (
                                <button
                                    type="button"
                                    onClick={() => { setShowEmailForm(false); setError(''); }}
                                    className="lgn-back"
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_back</span>
                                    {t('common.back')}
                                </button>
                            )}
                        </div>
                    )}

                    <p className="lgn-ver">v{APP_VERSION}</p>
                </div>
            </div>

            {/* ===== KANAN — 2 seksyen (arahan user 2026-08-21) =====
                (1) UI workspace SESAAT — mockup real menunjukkan pemilihan PUKAL
                    (banyak kad ditanda) + kawalan run automasi.
                (2) Teks USP: proses pukal + automasi penuh (killer USP, rujuk CLAUDE.md
                    "bulk-first: satu klik borong, sistem urus sendiri"). */}
            <div className="lgn-right">
                <div className="lgn-right-inner">
                    {/* Seksyen 1 — UI workspace SEBENAR (design sahaja, tiada logic) */}
                    <WorkspaceMock />

                    {/* Seksyen 2 — USP: proses pukal + automasi */}
                    <div className="lgn-usp">
                        <div className="lgn-usp-title">Satu platform. 6 formula. Automasi penuh.</div>
                        <p className="lgn-usp-text">Pilih puluhan produk sekali gus — SESAAT generate full prompt serentak, jana gambar &amp; video AI di Google Flow secara automatik, jadualkan, dan auto-post ke TikTok. Semuanya berjalan sequence &amp; parallel merentas beberapa profile browser — tanpa klik satu-satu.</p>
                    </div>
                </div>
            </div>

            <style>{`
                /* ============ HALAMAN ============ */
                .lgn-page {
                    display: grid;
                    grid-template-columns: 37fr 63fr;
                    gap: 20px;
                    padding: 20px;
                    min-height: 100vh;
                    box-sizing: border-box;
                    background: var(--s-ground);
                    /* Font UI biasa untuk SELURUH login (BUKAN Cal Sans tema
                       cal.com) — arahan user 2026-08-21. Semua teks login (tajuk,
                       butang, logo, input, mockup) mewarisi dari sini, jadi satu
                       tukaran = seluruh page. */
                    font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                    color: var(--s-ink);
                }

                /* ============ KIRI — borang ============ */
                .lgn-left {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--s-card);
                    border: 1px solid var(--s-line);
                    border-radius: var(--s-r-lg);
                    padding: 40px 28px;
                }
                .lgn-left-inner {
                    width: 100%;
                    max-width: 360px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }

                /* Jenama */
                .lgn-brand {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 28px;
                }
                .lgn-logo {
                    width: 40px; height: 40px;
                    border-radius: 10px;
                    background: var(--s-primary);
                    color: var(--s-primary-ink);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .lgn-brandname {
                    font-size: 20px;
                    font-weight: 700;
                    letter-spacing: .5px;
                    color: var(--s-ink);
                }

                /* Kawasan borang / entry */
                .lgn-formarea, .lgn-entry {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                }
                .lgn-entry { gap: 12px; }

                .lgn-title {
                    font-size: 20px;
                    font-weight: 600;
                    color: var(--s-ink);
                    margin: 0 0 6px;
                    letter-spacing: .3px;
                }
                .lgn-subtitle {
                    font-size: 13px;
                    color: var(--s-ink-2);
                    line-height: 1.5;
                    margin: 0 0 16px;
                }

                .lgn-form {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                /* Input */
                .lgn-input {
                    width: 100%;
                    height: 44px;
                    padding: 0 14px;
                    background: var(--s-fill);
                    border: 1px solid var(--s-line-strong);
                    border-radius: var(--s-r);
                    color: var(--s-ink);
                    font-size: 14px;
                    outline: none;
                    transition: border-color .18s ease;
                    font-family: inherit;
                    box-sizing: border-box;
                }
                .lgn-input::placeholder { color: var(--s-ink-3); }
                .lgn-input:focus { border-color: var(--s-ink-3); }

                /* Butang utama */
                .lgn-submit {
                    width: 100%;
                    height: 44px;
                    margin-top: 2px;
                    background: var(--s-primary);
                    color: var(--s-primary-ink);
                    font-size: 14px;
                    font-weight: 600;
                    border: none;
                    border-radius: var(--s-r);
                    cursor: pointer;
                    transition: opacity .18s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-family: inherit;
                }
                .lgn-submit:hover:not(:disabled) { opacity: .88; }
                .lgn-submit:disabled { opacity: .45; cursor: not-allowed; }

                /* Butang ghost (Google / Teruskan dengan Emel) */
                .lgn-google, .lgn-btn-ghost {
                    width: 100%;
                    height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    background: transparent;
                    color: var(--s-ink);
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid var(--s-line-strong);
                    border-radius: var(--s-r);
                    cursor: pointer;
                    transition: background .18s ease, border-color .18s ease;
                    font-family: inherit;
                }
                .lgn-google:hover:not(:disabled), .lgn-btn-ghost:hover {
                    background: var(--s-fill);
                    border-color: var(--s-ink-3);
                }
                .lgn-google:disabled { opacity: .6; cursor: not-allowed; }

                /* Pembahagi "atau" */
                .lgn-divider {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    color: var(--s-ink-3);
                    font-size: 12px;
                }
                .lgn-divider::before, .lgn-divider::after {
                    content: '';
                    flex: 1;
                    height: 1px;
                    background: var(--s-line-strong);
                }

                .lgn-terms {
                    font-size: 11px;
                    color: var(--s-ink-3);
                    line-height: 1.5;
                    margin: 4px 0 0;
                }
                .lgn-note {
                    font-size: 11px;
                    color: var(--s-ink-3);
                    line-height: 1.4;
                    margin: -4px 0 0;
                    text-align: left;
                }

                /* Spinner — warna ikut teks butang (currentColor) supaya jelas
                   pada butang gelap (tema terang) mahupun terang (tema gelap). */
                .lgn-spinner {
                    width: 20px; height: 20px;
                    border: 2.5px solid color-mix(in srgb, currentColor 22%, transparent);
                    border-top-color: currentColor;
                    border-radius: 50%;
                    animation: lgnSpin .7s linear infinite;
                }
                @keyframes lgnSpin { to { transform: rotate(360deg); } }

                /* Mesej */
                .lgn-msg {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 14px;
                    margin-bottom: 14px;
                    border-radius: var(--s-r);
                    font-size: 13px;
                    text-align: left;
                }
                .lgn-msg-error {
                    color: var(--s-stop);
                    background: var(--s-stop-soft);
                    border: 1px solid var(--s-stop-soft);
                }
                .lgn-msg-success {
                    color: var(--s-ok);
                    background: var(--s-ok-soft);
                    border: 1px solid var(--s-ok-soft);
                }

                /* Pautan */
                .lgn-link {
                    display: block;
                    width: 100%;
                    text-align: center;
                    margin-top: 14px;
                    font-size: 13px;
                    color: var(--s-ink-2);
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-weight: 500;
                    transition: color .18s;
                    font-family: inherit;
                }
                .lgn-link:hover { color: var(--s-ink); }

                .lgn-toggle-row {
                    margin-top: 16px;
                    text-align: center;
                    font-size: 13px;
                    color: var(--s-ink-2);
                }
                .lgn-toggle {
                    background: none;
                    border: none;
                    color: var(--s-ink);
                    font-weight: 700;
                    cursor: pointer;
                    margin-left: 4px;
                    font-size: 13px;
                    font-family: inherit;
                }

                .lgn-back {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    width: 100%;
                    margin-top: 16px;
                    padding: 11px;
                    background: none;
                    border: 1px solid var(--s-line-strong);
                    border-radius: var(--s-r);
                    color: var(--s-ink-2);
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background .18s, color .18s, border-color .18s;
                    font-family: inherit;
                }
                .lgn-back:hover {
                    background: var(--s-fill);
                    color: var(--s-ink);
                    border-color: var(--s-ink-3);
                }

                /* Sepanduk ejen */
                .lgn-ref-ok {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 14px;
                    margin-bottom: 14px;
                    border-radius: var(--s-r);
                    font-size: 13px;
                    text-align: left;
                    color: var(--s-ok);
                    background: var(--s-ok-soft);
                    border: 1px solid var(--s-ok-soft);
                }
                .lgn-ref-ok strong { font-weight: 700; }

                .lgn-ref-missing {
                    padding: 14px;
                    margin-bottom: 14px;
                    border-radius: var(--s-r);
                    background: var(--s-warn-soft);
                    border: 1px solid var(--s-warn-soft);
                    text-align: left;
                }
                .lgn-ref-missing-head {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: var(--s-warn);
                    font-size: 14px;
                    font-weight: 700;
                    margin-bottom: 6px;
                }
                .lgn-ref-missing-body {
                    font-size: 12px;
                    color: var(--s-ink-2);
                    line-height: 1.5;
                    margin: 0 0 12px;
                }
                .lgn-wa-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    width: 100%;
                    padding: 11px 14px;
                    background: #25D366;
                    color: #fff;
                    font-size: 13px;
                    font-weight: 600;
                    border-radius: var(--s-r);
                    text-decoration: none;
                    transition: opacity .18s ease;
                }
                .lgn-wa-btn:hover { opacity: .9; }

                .lgn-ver {
                    margin-top: 22px;
                    font-size: 11px;
                    color: var(--s-ink-3);
                }

                /* ============ KANAN — panel jenama gelap + carousel ============
                   #1C1C1C TETAP supaya panel kekal gelap dua-dua tema. Mockup
                   browser di dalam guna warna terang TETAP — "tangkap skrin"
                   terapung atas panel gelap; aksen ungu #7C6CF0 = warna SESAAT. */
                .lgn-right {
                    background: #1C1C1C;
                    border-radius: var(--s-r-lg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 24px 22px;
                    overflow: hidden;
                }
                .lgn-right-inner { width: 100%; max-width: 760px; display: flex; flex-direction: column; gap: 28px; }

                /* ===== Panel kanan v2: (1) UI workspace  (2) USP pukal+automasi ===== */
                /* Seksyen 1 — kandungan workspace (dalam .lgn-brw-page) */
                .lgn-ws { display: flex; flex-direction: column; height: 100%; gap: 10px; }
                .lgn-ws-bar { display: flex; align-items: center; gap: 8px; background: #262626; border-radius: 9px; padding: 8px 11px; }
                .lgn-ws-count { font-size: 10.5px; color: #fafafa; margin-right: auto; }
                .lgn-ws-count b { font-weight: 700; }
                .lgn-ws-act { font-size: 9.5px; font-weight: 600; color: #fafafa; background: rgba(255,255,255,.13); border-radius: 6px; padding: 4px 9px; }
                .lgn-ws-act:first-of-type { background: #7c6cf0; }
                .lgn-ws-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; flex: 1; }
                .lgn-ws-card { position: relative; background: #f6f6f9; border: 1px solid #eceaf2; border-radius: 9px; padding: 7px; display: flex; flex-direction: column; gap: 5px; }
                .lgn-ws-card.on { border-color: #7c6cf0; box-shadow: 0 0 0 1px #7c6cf0; }
                .lgn-ws-thumb { height: 40px; border-radius: 6px; background: #e6e6ec; }
                .lgn-ws-cline { height: 6px; width: 80%; border-radius: 3px; background: #dcdce2; }
                .lgn-ws-badge { font-size: 8px; font-weight: 700; letter-spacing: .04em; color: #16a34a; background: rgba(22,163,74,.1); border-radius: 4px; padding: 2px 5px; align-self: flex-start; }
                .lgn-ws-check { position: absolute; top: 5px; right: 5px; width: 16px; height: 16px; border-radius: 50%; background: #7c6cf0; display: none; place-items: center; }
                .lgn-ws-card.on .lgn-ws-check { display: grid; }
                .lgn-ws-check .material-symbols-outlined { font-size: 12px; color: #fff; }
                /* kawalan run dalam panel SESAAT */
                .lgn-ws-opt { display: flex; align-items: center; justify-content: space-between; font-size: 9.5px; color: #4b4b52; }
                .lgn-ws-tog { width: 20px; height: 12px; border-radius: 99px; background: #d6d6db; flex: none; position: relative; display: inline-block; }
                .lgn-ws-tog::after { content: ''; position: absolute; top: 2px; left: 2px; width: 8px; height: 8px; border-radius: 50%; background: #fff; }
                .lgn-ws-tog.on { background: #7c6cf0; }
                .lgn-ws-tog.on::after { left: 10px; }
                /* Seksyen 2 — USP */
                .lgn-usp { text-align: center; }
                .lgn-usp-title { font-size: 23px; font-weight: 700; color: #fafafa; line-height: 1.25; letter-spacing: .2px; }
                .lgn-usp-text { font-size: 13px; color: rgba(250,250,250,.6); line-height: 1.6; margin: 9px auto 0; max-width: 480px; }

                /* ===== Section 1: mockup UI/UX Workspace SEBENAR (warna terang TETAP) ===== */
                .lgn-app { display: flex; background: #fff; border-radius: 14px; overflow: hidden; box-shadow: 0 14px 36px rgba(0,0,0,.42); }
                .lgn-app-side { width: 40px; flex: none; background: #fafafa; border-right: 1px solid #eee; display: flex; flex-direction: column; align-items: center; padding: 8px 0; gap: 3px; overflow: hidden; }
                .lgn-app-side-ic { width: 26px; height: 26px; border-radius: 7px; display: grid; place-items: center; color: #a8a8b0; flex: none; }
                .lgn-app-side-ic .material-symbols-outlined { font-size: 16px; }
                .lgn-app-logo { width: 26px; height: 26px; border-radius: 7px; background: #262626; color: #fafafa; display: grid; place-items: center; margin: 2px 0 4px; flex: none; }
                .lgn-app-logo .material-symbols-outlined { font-size: 15px; }
                .lgn-app-main { flex: 1; min-width: 0; padding: 15px 17px; display: flex; flex-direction: column; gap: 12px; }
                .lgn-app-head { display: flex; align-items: center; gap: 10px; }
                .lgn-app-title { font-size: 18px; font-weight: 700; color: #1a1a1a; }
                .lgn-app-group { display: flex; align-items: center; gap: 5px; font-size: 10.5px; color: #4b4b52; border: 1px solid #e5e5ea; border-radius: 8px; padding: 4px 9px; }
                .lgn-app-group b { font-weight: 700; }
                .lgn-app-group .material-symbols-outlined { font-size: 13px; color: #8a8a92; }
                .lgn-app-tabs { display: flex; gap: 5px; }
                .lgn-app-tab { display: flex; align-items: center; gap: 5px; font-size: 10.5px; font-weight: 600; color: #6a6a72; border-radius: 8px; padding: 6px 10px; }
                .lgn-app-tab .material-symbols-outlined { font-size: 14px; }
                .lgn-app-tab b { font-weight: 700; color: #9a9aa2; }
                .lgn-app-tab.on { background: #f1f1f4; color: #1a1a1a; }
                .lgn-app-box { flex: none; border: 1px solid #ececf0; border-radius: 12px; padding: 13px 14px; display: flex; flex-direction: column; gap: 10px; }
                .lgn-app-boxtop { display: flex; align-items: center; gap: 12px; }
                .lgn-app-add { display: flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 600; color: #333; border: 1px solid #e0e0e6; border-radius: 99px; padding: 5px 11px; }
                .lgn-app-add .material-symbols-outlined { font-size: 13px; }
                .lgn-app-dim { font-size: 10px; color: #a0a0a8; }
                .lgn-app-formlabel { font-size: 10.5px; font-weight: 600; color: #333; }
                .lgn-app-formlabel em { font-style: normal; font-weight: 400; color: #a0a0a8; }
                .lgn-app-formulas { display: flex; gap: 6px; flex: none; align-items: flex-start; }
                .lgn-fcard { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
                /* Thumbnail STATIK tinggi TETAP (semua sama). Imej = JPG kecil
                   (~2-13KB) dari Hosting (di-cache), BUKAN video, BUKAN Firebase
                   Storage — hampir zero cost. Arahan user 2026-08-21. */
                .lgn-fthumb { position: relative; flex: none; aspect-ratio: 9 / 16; border-radius: 9px; background: #d6d6dc; background-size: cover; background-position: center; overflow: hidden; }
                .lgn-fthumb--new { background: #f4f4f6; border: 1px dashed #cdcdd4; display: grid; place-items: center; color: #b0b0b8; }
                .lgn-fthumb--new .material-symbols-outlined { font-size: 20px; }
                .lgn-fhook { position: absolute; top: 7px; left: 5px; right: 5px; font-size: 8px; font-weight: 800; color: #fff; line-height: 1.15; text-shadow: 0 1px 2px rgba(0,0,0,.55); text-align: center; }
                .lgn-fdur { position: absolute; bottom: 5px; right: 5px; font-size: 7.5px; font-weight: 700; color: #fff; background: rgba(0,0,0,.55); border-radius: 4px; padding: 1px 4px; }
                .lgn-fpen { position: absolute; top: 5px; right: 5px; width: 15px; height: 15px; border-radius: 5px; background: rgba(255,255,255,.85); display: grid; place-items: center; color: #555; }
                .lgn-fpen .material-symbols-outlined { font-size: 10px; }
                .lgn-fname { font-size: 9px; font-weight: 700; color: #1a1a1a; display: flex; align-items: center; gap: 2px; white-space: nowrap; }
                .lgn-fbadge { font-size: 6.5px; font-weight: 700; letter-spacing: .02em; border-radius: 3px; padding: 1px 2px; flex: none; }
                .lgn-fbadge--beta { color: #7c6cf0; background: rgba(124,108,240,.12); }
                .lgn-fbadge--new { color: #16a34a; background: rgba(22,163,74,.12); }
                .lgn-fsub { font-size: 8px; color: #a0a0a8; }
                .lgn-app-bottom { display: flex; gap: 8px; }
                .lgn-app-pill { display: flex; align-items: center; gap: 5px; font-size: 10px; color: #4b4b52; border: 1px solid #e5e5ea; border-radius: 99px; padding: 5px 11px; }
                .lgn-app-pill .material-symbols-outlined { font-size: 13px; color: #8a8a92; }
                .lgn-app-toggle { width: 22px; height: 12px; border-radius: 99px; background: #d6d6db; position: relative; display: inline-block; }
                .lgn-app-toggle::after { content: ''; position: absolute; top: 2px; left: 2px; width: 8px; height: 8px; border-radius: 50%; background: #fff; }
                .lgn-right-brand { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
                .lgn-right-logo {
                    width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
                    background: rgba(255,255,255,.12); color: #fafafa;
                    display: flex; align-items: center; justify-content: center;
                }
                .lgn-right-title { font-size: 16px; font-weight: 600; color: #fafafa; line-height: 1.2; }
                .lgn-right-sub { font-size: 12px; color: rgba(250,250,250,.5); margin-top: 1px; }

                .lgn-stage { overflow: hidden; border-radius: 12px; position: relative; }
                /* Anak panah manual (kekal, tambahan kepada auto-slide + bulatan) */
                .lgn-arrow {
                    position: absolute; top: 50%; transform: translateY(-50%);
                    width: 30px; height: 30px; border-radius: 50%; border: none;
                    background: rgba(17,17,17,.5); color: #fff; cursor: pointer;
                    display: grid; place-items: center; z-index: 10;
                    transition: background .18s ease;
                    box-shadow: 0 2px 8px rgba(0,0,0,.25);
                }
                .lgn-arrow:hover { background: rgba(17,17,17,.82); }
                .lgn-arrow .material-symbols-outlined { font-size: 20px; }
                .lgn-arrow--prev { left: 8px; }
                .lgn-arrow--next { right: 8px; }
                .lgn-track { display: flex; transition: transform .55s cubic-bezier(.4,0,.2,1); }
                .lgn-slide { flex: 0 0 100%; min-width: 0; }

                /* Mockup browser — warna terang TETAP (kekal terang atas panel gelap) */
                .lgn-brw {
                    position: relative; background: #fff; border-radius: 12px;
                    overflow: hidden; box-shadow: 0 12px 32px rgba(0,0,0,.4);
                }
                .lgn-brw-bar {
                    display: flex; align-items: center; gap: 6px; padding: 9px 12px;
                    background: #f4f4f6; border-bottom: 1px solid #e8e8ec;
                }
                .lgn-dot3 { width: 7px; height: 7px; border-radius: 99px; background: #d6d6db; flex: none; }
                .lgn-brw-url {
                    flex: 1; margin: 0 8px; height: 20px; border-radius: 99px;
                    background: #fff; border: 1px solid #e8e8ec;
                    display: flex; align-items: center; padding: 0 10px; min-width: 0;
                }
                .lgn-brw-url span { font-size: 10px; color: #9a9aa2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .lgn-brw-zap {
                    width: 20px; height: 20px; border-radius: 6px; flex: none;
                    display: grid; place-items: center;
                    background: rgba(124,108,240,.14); color: #7c6cf0;
                }
                .lgn-brw-body { display: flex; height: 300px; }
                .lgn-brw-page { flex: 1; padding: 14px; min-width: 0; }
                .lgn-brw-panel { width: 122px; flex: none; border-left: 1px solid #eee; display: flex; flex-direction: column; }
                .lgn-brw-panelhead {
                    display: flex; align-items: center; gap: 5px;
                    padding: 7px 10px; background: rgba(124,108,240,.12);
                }
                .lgn-brw-panelhead span:last-child { font-size: 9.5px; font-weight: 700; letter-spacing: .05em; color: #7c6cf0; }
                .lgn-brw-panelhead .material-symbols-outlined { color: #7c6cf0; }
                .lgn-brw-panelbody { padding: 10px; display: flex; flex-direction: column; gap: 7px; flex: 1; }
                .lgn-actbtn {
                    height: 26px; border-radius: 7px; background: #7c6cf0; margin-top: auto;
                    display: flex; align-items: center; justify-content: center;
                    color: #fff; font-size: 10px; font-weight: 600;
                }
                /* Panel SESAAT — status per langkah */
                .lgn-chk { display: flex; align-items: center; gap: 4px; font-size: 9.5px; color: #4b4b52; }
                .lgn-chk .material-symbols-outlined { font-size: 12px; color: #7c6cf0; }
                .lgn-panel-progress { display: flex; flex-direction: column; gap: 5px; }
                .lgn-pp-text { font-size: 9.5px; color: #4b4b52; }
                .lgn-pp-bar { height: 5px; border-radius: 3px; background: #eceaf8; overflow: hidden; }
                .lgn-pp-bar span { display: block; width: 100%; height: 100%; background: #7c6cf0; }
                .lgn-pp-count { font-size: 9px; color: #9a9aa2; }
                .lgn-panel-lines { display: flex; flex-direction: column; gap: 3px; }
                .lgn-pl-strong { font-size: 11px; font-weight: 600; color: #333; }
                .lgn-pl-sub { font-size: 9px; color: #9a9aa2; }
                .lgn-pl-up { font-size: 9.5px; color: #4b4b52; }
                .lgn-pl-done { display: flex; align-items: center; gap: 4px; font-size: 9.5px; font-weight: 600; color: #16a34a; }
                .lgn-pl-done .material-symbols-outlined { font-size: 12px; color: #16a34a; }

                /* Sidebar app SESAAT (slaid app.sesaat.ai) */
                .lgn-side { width: 46px; flex: none; background: #fafafa; border-right: 1px solid #eee; display: flex; flex-direction: column; align-items: center; padding: 9px 0; gap: 7px; }
                .lgn-side-logo { width: 26px; height: 26px; border-radius: 7px; background: #262626; color: #fafafa; display: grid; place-items: center; margin-bottom: 3px; }
                .lgn-side-ic { width: 28px; height: 28px; border-radius: 7px; display: grid; place-items: center; color: #b4b4bc; }
                .lgn-side-ic .material-symbols-outlined { font-size: 17px; }
                .lgn-side-ic.on { background: rgba(124,108,240,.14); color: #7c6cf0; }
                /* Sub-tajuk dalam panel SESAAT */
                .lgn-panel-sub { font-size: 10px; font-weight: 700; color: #262626; margin-bottom: 1px; }
                /* Mod GELAP (Flow) — kawasan halaman gelap; bar + panel kekal terang */
                .lgn-brw--dark .lgn-brw-page { background: #1e1e20; }
                .lgn-brw--dark .lgn-tile { background: #2c2c30; }
                .lgn-brw--dark .lgn-tile--vid { background: #37333f; }

                /* Kandungan "halaman" kiri per langkah — kandungan sebenar */
                /* 1 — Produk TikTok Shop */
                .lgn-pg-prod { display: flex; gap: 14px; height: 100%; align-items: center; }
                .lgn-pg-img { width: 128px; height: 128px; border-radius: 12px; background: #ececf0; flex: none; display: grid; place-items: center; color: #c4c4cc; }
                .lgn-pg-img .material-symbols-outlined { font-size: 34px; }
                .lgn-pg-lines { flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0; }
                .lgn-pg-name { font-size: 13px; font-weight: 600; color: #262626; line-height: 1.3; }
                .lgn-pg-pricerow { display: flex; align-items: baseline; gap: 6px; }
                .lgn-pg-price { font-size: 17px; font-weight: 700; color: #fe2c55; }
                .lgn-pg-was { font-size: 10px; color: #b8b8be; text-decoration: line-through; }
                .lgn-pg-disc { font-size: 9px; font-weight: 700; color: #fe2c55; background: rgba(254,44,85,.1); padding: 1px 5px; border-radius: 4px; align-self: center; }
                .lgn-pg-rate { display: flex; align-items: center; gap: 3px; font-size: 10px; color: #9a9aa2; }
                .lgn-pg-rate .material-symbols-outlined { font-size: 12px; color: #f5b301; font-variation-settings: 'FILL' 1; }
                .lgn-pg-variants { display: flex; gap: 5px; margin-top: 1px; }
                .lgn-pg-variants span { font-size: 9px; color: #4b4b52; border: 1px solid #e0e0e6; border-radius: 6px; padding: 3px 8px; }
                .lgn-pg-variants span:first-child { border-color: #fe2c55; color: #fe2c55; background: rgba(254,44,85,.06); }
                .lgn-pg-buy { align-self: flex-start; margin-top: 5px; padding: 7px 20px; border-radius: 99px; background: #fe2c55; color: #fff; font-size: 11px; font-weight: 700; }
                /* 2 — Kad Generate SESAAT (workspace) */
                .lgn-pg-work { display: flex; flex-direction: column; gap: 8px; height: 100%; justify-content: center; }
                .lgn-pg-worktop { display: flex; align-items: center; gap: 9px; }
                .lgn-pg-workthumb { width: 36px; height: 36px; border-radius: 8px; background: #ececf0; flex: none; }
                .lgn-pg-cardname { font-size: 12.5px; font-weight: 600; color: #262626; }
                .lgn-pg-field { display: flex; flex-direction: column; gap: 3px; background: #f6f6f9; border-radius: 8px; padding: 8px 10px; }
                .lgn-pg-field > span:last-child { font-size: 11px; color: #4b4b52; line-height: 1.4; }
                .lgn-pg-tag { font-size: 8.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #7c6cf0; }
                .lgn-pg-chips { display: flex; gap: 6px; margin-top: 1px; }
                .lgn-pg-chips span { font-size: 9px; font-weight: 600; color: #7c6cf0; background: rgba(124,108,240,.1); border-radius: 6px; padding: 4px 9px; }
                /* 3 — Grid Flow (gambar + video) */
                .lgn-pg-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; height: 100%; }
                .lgn-tile { border-radius: 8px; background: #ececf0; display: grid; place-items: center; }
                .lgn-tile--vid { background: #ddd8f5; }
                .lgn-tile--vid .material-symbols-outlined { font-size: 22px; color: #7c6cf0; font-variation-settings: 'FILL' 1; }
                /* 4 — Schedule (calendar sebenar) */
                .lgn-pg-sched { display: flex; flex-direction: column; height: 100%; gap: 6px; }
                .lgn-cal-head { display: flex; align-items: center; justify-content: space-between; font-size: 12px; font-weight: 700; color: #262626; padding: 0 2px; }
                .lgn-cal-head .material-symbols-outlined { font-size: 15px; color: #9a9aa2; }
                .lgn-cal-week { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
                .lgn-cal-week span { text-align: center; font-size: 8.5px; font-weight: 700; color: #b4b4bc; }
                .lgn-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); grid-auto-rows: 1fr; gap: 4px; flex: 1; }
                .lgn-cell { border-radius: 6px; background: #f2f2f5; display: grid; place-items: center; }
                .lgn-cell b { font-size: 9.5px; font-weight: 500; color: #a8a8b0; }
                .lgn-cell--on { background: #7c6cf0; }
                .lgn-cell--on em { font-size: 9px; font-style: normal; font-weight: 700; color: #fff; }
                /* 5 — TikTok upload */
                .lgn-pg-upload { display: flex; gap: 14px; height: 100%; align-items: center; }
                .lgn-up-video { width: 104px; height: 168px; border-radius: 11px; background: #ddd8f5; flex: none; display: grid; place-items: center; }
                .lgn-up-video .material-symbols-outlined { font-size: 32px; color: #7c6cf0; font-variation-settings: 'FILL' 1; }
                .lgn-up-fields { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 7px; }
                .lgn-up-cap { font-size: 11px; color: #4b4b52; line-height: 1.5; background: #f6f6f9; border-radius: 8px; padding: 9px 10px; }
                .lgn-up-meta { display: flex; align-items: center; gap: 4px; font-size: 9.5px; color: #9a9aa2; }
                .lgn-up-meta .material-symbols-outlined { font-size: 13px; }
                .lgn-up-post { align-self: flex-start; margin-top: 2px; padding: 7px 22px; border-radius: 7px; background: #fe2c55; color: #fff; font-size: 11px; font-weight: 700; }

                /* Kursor automasi — animasi HANYA berjalan pada slide aktif, jadi
                   ia mula semula (sync) setiap kali slide bertukar. */
                .lgn-cursor { position: absolute; left: 22%; top: 66%; opacity: 0; z-index: 5; pointer-events: none; }
                .lgn-cursor svg { display: block; }
                .lgn-ripple {
                    position: absolute; left: calc(100% - 61px); top: 93%;
                    width: 30px; height: 30px; margin: -15px 0 0 -15px;
                    border-radius: 50%; background: #7c6cf0; opacity: 0; z-index: 4; pointer-events: none;
                }
                .lgn-slide--active .lgn-cursor { opacity: 1; animation: lgnCurMove 3.4s ease-in-out; }
                .lgn-slide--active .lgn-cursor svg { animation: lgnCurPress 3.4s ease-in-out; }
                .lgn-slide--active .lgn-ripple { animation: lgnRip 3.4s ease-in-out; }
                @keyframes lgnCurMove {
                    0%, 16% { left: 22%; top: 66%; }
                    45%, 58% { left: calc(100% - 66px); top: 92%; }
                    85%, 100% { left: 22%; top: 66%; }
                }
                @keyframes lgnCurPress {
                    0%, 47% { transform: scale(1); }
                    52% { transform: scale(.8); }
                    58%, 100% { transform: scale(1); }
                }
                @keyframes lgnRip {
                    0%, 47% { opacity: 0; transform: scale(.3); }
                    53% { opacity: .5; transform: scale(1); }
                    72%, 100% { opacity: 0; transform: scale(1.9); }
                }

                /* Kaki: label langkah + bulatan penunjuk */
                .lgn-caption { text-align: center; margin-top: 22px; }
                .lgn-feat-name { font-size: 23px; font-weight: 700; color: #fafafa; letter-spacing: .2px; }
                .lgn-feat-desc { font-size: 13px; color: rgba(250,250,250,.55); margin-top: 5px; line-height: 1.5; }
                .lgn-dots { display: flex; gap: 5px; justify-content: center; margin-top: 16px; }
                .lgn-dots button { height: 6px; width: 6px; border-radius: 99px; border: none; padding: 0; background: rgba(255,255,255,.25); cursor: pointer; transition: all .3s ease; }
                .lgn-dots button.on { width: 18px; background: #fafafa; }

                @media (prefers-reduced-motion: reduce) {
                    .lgn-track { transition: none; }
                    .lgn-slide--active .lgn-cursor,
                    .lgn-slide--active .lgn-cursor svg,
                    .lgn-slide--active .lgn-ripple { animation: none; }
                    .lgn-cursor { opacity: 0; }
                }

                /* ============ RESPONSIF ============ */
                @media (max-width: 900px) {
                    .lgn-page {
                        grid-template-columns: 1fr;
                        gap: 0;
                        padding: 12px;
                    }
                    .lgn-right { display: none; }
                    .lgn-left {
                        min-height: calc(100vh - 24px);
                        padding: 32px 20px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Login;
